import {
    SetIntervalCommand,
    SetTimeoutCommand,
    ClearTimeoutCommand,
    ClearIntervalCommand,
    TimerResponseType,
    TimerErrorResponse,
    TimerResponse
} from "flow/workers/TimerWorkerCommand";

import logger from "flow/logger";
import * as timerWorkerRegistry from "flow/workers/timerWorkerRegistry";

type CallbackTable = {
    [id: number]: () => void;
};

function isTimerResponse(response: TimerResponse | TimerErrorResponse): response is TimerResponse {
    return (response as TimerResponse).type === TimerResponseType.Tick;
}

export class BackgroundTimer {
    private worker: Worker;
    private nextFreeId: number = 0;
    private callbacks: CallbackTable = {};

    private initWorkerIfNeeded = () => {
        if (!this.worker) {
            console.log("Creating worker");
            this.worker = timerWorkerRegistry.createTimerWorker();
            this.worker.addEventListener("message", this.onGotMessage);
        }
    };

    private getNextId = () => {
        return ++this.nextFreeId;
    };

    setInterval = (func: () => void, delay: number) => {
        this.initWorkerIfNeeded();

        const timerId = this.getNextId();
        this.callbacks[timerId] = func;

        this.worker.postMessage(new SetIntervalCommand(delay, timerId));

        return timerId;
    };

    setTimeout = (func: () => void, delay: number) => {
        this.initWorkerIfNeeded();

        const timerId = this.getNextId();
        this.callbacks[timerId] = func;

        this.worker.postMessage(new SetTimeoutCommand(delay, timerId));

        return timerId;
    };

    private onGotMessage = (event: MessageEvent) => {
        const response: TimerResponse | TimerErrorResponse = event.data;

        if (isTimerResponse(response)) {
            this.processTick(response.timerId);
        } else {
            logger.error("BackgroundTimer Error", response.message);
        }
    };

    private processTick = (timerId: number) => {
        if (this.callbacks[timerId]) {
            this.callbacks[timerId]();
        }
    };

    clearInterval = (timerId: number) => {
        this.worker.postMessage(new ClearIntervalCommand(timerId));
    };

    clearTimeout = (timerId: number) => {
        this.worker.postMessage(new ClearTimeoutCommand(timerId));
    };

    destroy = () => {
        if (this.worker) {
            this.worker.removeEventListener("message", this.onGotMessage);
            this.worker.terminate();
            this.worker = undefined;
        }
    };
}

const backgroundTimer = new BackgroundTimer();
export default backgroundTimer;
